import './preview_collaboration_wrapper.less';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  Fragment
} from 'react';
import {
  useVeltClient,
  VeltProvider,
  VeltComments,
  VeltCommentsSidebar,
  VeltPresence,
  VeltCommentTool,
  VeltCursor,
  VeltHuddle,
  VeltHuddleTool,
  VeltSidebarButton
} from '@veltdev/react';

import { getRouter } from '../lib/global_store.js';
import { setActiveTabId } from '../action_creators/viewer.js';
import {
  getSubpageById,
  getCurrentSubpage,
  getActiveOverlayContent,
  getPreviewCollaborationSettings,
  getSectionById,
  getSections
} from '../reducers/index.js';

/*************************************************************************************************/
/*** PROPS ***************************************************************************************/
/*************************************************************************************************/

const PROP_TYPES = {
  // This contains the UI we want to wrap with the Velt wrapper.
  children: PropTypes.node.isRequired
};

const mapStateToProps = (reduxState, props) => {
  const currentSubpage = getCurrentSubpage(reduxState);
  const activeOverlayContent = getActiveOverlayContent(reduxState);
  const settings = getPreviewCollaborationSettings(reduxState);

  return {
    settings: settings,
    veltLocation: {
      subpageId: currentSubpage.id,
      activeOverlayContentUuid: activeOverlayContent && activeOverlayContent.uuid,
    },
    getSubpageById: (subpageId) => getSubpageById(reduxState, subpageId),
    getSectionById: (sectionId) => getSectionById(reduxState, sectionId),
    getAllSections: () => getSections(reduxState),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setActiveTabId: (sectionId, tabId) => dispatch(setActiveTabId(sectionId, tabId))
  };
};


/*************************************************************************************************/
/*** STATE ***************************************************************************************/
/*************************************************************************************************/

const PreviewCollaborationWrapper = (props) => {
  if(!props.settings || !props.settings.isEnabled){
    return props.children;
  }

  return (
    <VeltProvider apiKey={props.settings.veltSettings.apiKey}>
      <VeltInit {...props} />
    </VeltProvider>
  );
};

// We need to run useEffect after the provider has been initialized, which is why we split the state component into two here.
const VeltInit = (props) => {
  const { client } = useVeltClient();

  useEffect(() => {
    if (client) {
      client.setDocumentId(props.settings.veltSettings.documentId);
      client.identify(props.settings.veltSettings.user, { replaceContacts: true })
        .then((res) => {
          console.log(`User ${props.settings.veltSettings.user.email} authenticated with velt.`);
        })
        .catch((err) => {
          console.error(`User ${props.settings.veltSettings.user.email} failed to authenticate with velt.`, err);
        });

      // enables Chat-GPT capabilities
      if (props.settings.isGptEnabled) {
        client.getRewriterElement().enableRewriter();
      }

      client.getCommentElement().enableTextComments(true);
      client.getSelectionElement().enableLiveSelection(true);
    }
  }, [client]);

  useEffect(() => {
    if (client) {
      client.setLocation(props.veltLocation);
    }
  }, [{ client: client, veltLocation: props.veltLocation }]);

  const onVeltSidebarCommentClick = (event) => {
    if (!event || !event.location) {
      return;
    }

    const subpage = props.getSubpageById(event.location.subpageId);

    if (event.location.activeOverlayContentUuid) {
      getRouter().navigateToOverlay(
        subpage,
        event.location.activeOverlayContentUuid,
        { trigger: true, replace: true }
      );
    } else {
      getRouter().navigateToSubpage(
        subpage,
        { trigger: true, replace: true }
      );
    }

    const { sectionId, tabId } = event.context;
    if (sectionId && tabId) {
      props.setActiveTabId(sectionId, tabId);
    }
  };

  const onVeltCommentAdd = (comment => {
    const xpath = comment.elementRef.xpath;
    if (!xpath) {
      return;
    }

    // Find the highest section parent using XPath
    // This XPath finds the closest ancestor (or self) that is a section element
    const sectionXPath = xpath + '/ancestor-or-self::section[1]';
    const sectionElement = document.evaluate(sectionXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;

    if (!sectionElement || !sectionElement.id) {
      return;
    }

    // Get the section ID (remove 'section-id-' prefix)
    const sectionId = sectionElement.id.replace('section-id-', '');

    // Find the section in Redux state
    const section = props.getSectionById(sectionId);
    if (!section) {
      return;
    }

    let tabId = null;
    let parentSectionId = null;

    // Check if section is inside a tab
    if (section._isInsideTab) {

      // Find the parent tabs section that contains this section
      // We need to look for a tabs section that has this section as a child
      const allSections = props.getAllSections();
      const parentTabsSection = allSections.find(s =>
        s.type === 'tabs' &&
        s.tabs &&
        s.tabs.some(tab =>
          tab.content &&
          tab.content.sectionId === sectionId
        )
      );

      if (parentTabsSection) {
        // Find which tab contains this section
        const containingTab = parentTabsSection.tabs.find(tab =>
          tab.content &&
          tab.content.sectionId === sectionId
        );

        if (containingTab) {
          tabId = containingTab.id;
        }

        parentSectionId = parentTabsSection.id;
      }
    } else if (section.type === 'tabs') {
      // If this is a tabs section itself, get its active tab
      const activeTabId = section.activeTabId;
      if (activeTabId) {
        parentSectionId = activeTabId;
      }
    }

    comment.addContext({
      sectionId: parentSectionId,
      ...(tabId && { tabId })
    });
  });

  const state = {
    children: props.children,
    email: props.settings.veltSettings.user.email,

    onVeltSidebarCommentClick,
    onVeltCommentAdd,
  }

  return <UI {...state} />;
};

PreviewCollaborationWrapper.propTypes = PROP_TYPES;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PreviewCollaborationWrapper);

/*************************************************************************************************/
/*** UI ******************************************************************************************/
/*************************************************************************************************/

// Do we want to control which features are enabled?
const UI = (state) => {
  return (
    <Fragment>
      {/* enables comments display on the page */}
      <VeltComments onCommentAdd={state.onVeltCommentAdd} />
      {/* enables the comment sidebar drawer component */}
      <VeltCommentsSidebar onCommentClick={state.onVeltSidebarCommentClick} />
      <VeltHuddle />
      {/* shows user cursor position on the screen */}
      <VeltCursor />

      {state.children}

      <div id="velt-toolbar" className="velt-z-index-hack" data-velt-comment-disabled >
        <div className='message'>
          You are visiting this website as <span className='email'>{state.email}</span>
        </div>
        <div className='velt-components-wrapper'>
          <VeltPresence />
          <VeltCommentTool />
          <VeltHuddleTool />
          <VeltSidebarButton />
        </div>
      </div>
    </Fragment>
  );
};
